import { readTaskContext, clinicCallOf } from './task';
import type { SpruceCallTaskLike } from '../../../scripts/spruce-rows.mjs';

// WF10's record of what a monthly claim rests on, read ONLY from claim_evidence (a key WF17 / WF50 refuse to anyone
// but a workflow); the task's other context is session-writable and never shown. An unknown shape shows nothing.
export const CLAIM_TEMPLATE = 'rec_monthly_claims';
export const CLAIM_NOTE = 'Automated prompt, not an attestation of billability: verify the active care before filing.';

export interface ClaimEvidence {
  summary: string | null;
  details: string[];
  callIds: string[];
  callsCounted: number | null;
}

type ClaimTaskLike = {
  template_code?: unknown;
  template?: { template_code?: unknown } | null;
  context_json?: unknown;
};
type CallTask = SpruceCallTaskLike & { task_id?: unknown; status?: unknown };
// The patient's call tasks as read for the re-check: rows and whether the read was whole, or its state.
export type CallRead = { tasks: readonly CallTask[]; complete: boolean } | 'loading' | 'error';

const DOS_FROM: Record<string, string> = {
  call: 'a clinic-line call',
  outreach: 'a staff message',
  task: 'a completed task',
};

const count = (v: unknown): number | null => (typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : null);
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const dayOf = (v: unknown): string | null => {
  if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(v)) return null;
  const t = Date.parse(v.slice(0, 10));
  return Number.isFinite(t)
    ? new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
    : null;
};
const monthOf = (v: unknown): string | null => {
  const m = typeof v === 'string' ? /^(\d{4})-(\d{2})$/.exec(v) : null;
  if (!m || Number(m[2]) < 1 || Number(m[2]) > 12) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1))
    .toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};
// The column or the view's nested code, never a code written into the context.
const codeOf = (task: ClaimTaskLike): string => String(task.template_code || task.template?.template_code || '');

export function claimEvidenceOf(task: ClaimTaskLike | null | undefined): ClaimEvidence | null {
  if (!task || codeOf(task) !== CLAIM_TEMPLATE) return null;
  const raw = readTaskContext(task.context_json)?.claim_evidence;
  const ev = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw as Record<string, unknown> : null;
  if (!ev) return null;

  const parts: string[] = [];
  const staff = count(ev.staff_outreach);
  const tasks = count(ev.task_completions);
  const calls = count(ev.calls);
  if (staff) parts.push(plural(staff, 'staff message', 'staff messages'));
  if (tasks) parts.push(plural(tasks, 'completed task', 'completed tasks'));
  // Spruce marks an outgoing call answered when the voicemail picks up too: the coordinator sees how many there are.
  const outgoing = count(ev.calls_outbound);
  const outShown = outgoing && calls && outgoing <= calls ? outgoing : null;
  if (calls) parts.push(plural(calls, 'clinic-line call', 'clinic-line calls') + (outShown ? ` (${outShown} outgoing, may have reached voicemail)` : ''));
  const known = typeof ev.dos_from === 'string' && Object.hasOwn(DOS_FROM, ev.dos_from) ? DOS_FROM[ev.dos_from] : undefined;
  const from = known && ev.dos_from === 'call' && ev.dos_direction === 'outbound' ? 'an outgoing clinic-line call' : known;
  const dos = dayOf(ev.dos);
  const dosText = from ? ` · date of service ${dos ? `${dos}, ` : ''}from ${from}` : '';
  const month = monthOf(ev.month);
  // A snapshot: WF10 builds the claim once and never re-evaluates a month that has one.
  const summary = parts.length
    ? `Active care ${month ? `in ${month}` : 'that month'}, as counted when this claim was created: ${parts.join(', ')}${dosText}`
    : null;
  const details: string[] = [];
  const gone = count(ev.calls_cancelled);
  const goneOut = count(ev.calls_cancelled_outbound);
  // Older claims carry no outgoing count: all of their cancelled calls were incoming.
  const split = gone != null && goneOut != null && goneOut <= gone;
  const goneIn = split && gone != null && goneOut != null ? gone - goneOut : gone;
  if (goneIn) details.push(`${plural(goneIn, 'call', 'calls')} cancelled as not this patient`);
  if (split && goneOut) details.push(`${plural(goneOut, 'outgoing call', 'outgoing calls')} cancelled as not reaching the patient`);
  const refused = count(ev.calls_refused);
  if (refused) details.push(`${plural(refused, 'call record', 'call records')} not counted (not stamped, no length, or changed by hand)`);
  if (!summary && !details.length) return null;
  const callIds = Array.isArray(ev.call_task_ids) ? ev.call_task_ids.filter((x) => x != null && x !== '').map(String) : [];
  return {
    summary, details, callIds, callsCounted: calls,
  };
}

// Re-checks the calls a claim counted against the patient's call tasks read now. Returns the warnings to show; never
// an all-clear it cannot prove (a failed or partial read says so). A counted call no longer completed, voided, or
// missing from a whole read is flagged.
export function claimRecheckOf(ev: ClaimEvidence | null, read: CallRead): { warning: string | null; note: string | null } {
  if (!ev || !ev.callIds.length || read === 'loading') return { warning: null, note: null };
  const capped = ev.callsCounted != null && ev.callsCounted > ev.callIds.length
    ? `Only ${ev.callIds.length} of the ${ev.callsCounted} counted calls are re-checked here.`
    : null;
  if (read === 'error' || !read.complete) {
    return { warning: 'Could not check for calls removed since this claim was created — re-check before filing', note: capped };
  }
  const byId = new Map(read.tasks.map((t) => [String(t.task_id), t]));
  const changed = ev.callIds.filter((id) => {
    const t = byId.get(id);
    return !t || t.status !== 'completed' || !!clinicCallOf(t)?.voided;
  }).length;
  const warning = changed
    ? `${changed === 1 ? '1 call counted here has' : `${changed} calls counted here have`} since been cancelled or changed — re-check before filing`
    : null;
  return { warning, note: capped };
}
